import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Account } from '../../types';

interface SetupChecklistProps {
  accounts: Account[];
}

const ITEMS: { label: string; cta?: string; getChecked: (props: SetupChecklistProps) => boolean; path?: string }[] = [
  { label: 'Create your account', getChecked: () => true },
  { label: 'Add your first asset', cta: 'Add an asset →', getChecked: (p) => p.accounts.some((a) => a.type === 'asset'), path: '/assets' },
  { label: 'Add a liability', cta: 'Add a liability →', getChecked: (p) => p.accounts.some((a) => a.type === 'liability'), path: '/liabilities' },
  {
    label: 'Add your business equity',
    cta: 'Add business equity →',
    getChecked: (p) =>
      p.accounts.some((a) => a.account_type === 'business') ||
      localStorage.getItem('wealthini_no_business') === 'true',
    path: '/assets',
  },
];

const SetupChecklist: React.FC<SetupChecklistProps> = (props) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('wealthini_checklist_dismissed') === 'true');

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('wealthini_checklist_dismissed', 'true');
  };

  const checked = ITEMS.map((item) => item.getChecked(props));
  const checkedCount = checked.filter(Boolean).length;

  if (dismissed || checkedCount === 4) {
    return null;
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-semibold text-white">Your setup checklist</h3>
        <button
          type="button"
          onClick={handleDismiss}
          className="ml-auto text-white/40 hover:text-white transition-colors"
          aria-label="Dismiss checklist"
        >
          ✕
        </button>
      </div>
      <p className="text-sm text-white/50 mb-4">5 minutes to your complete financial picture</p>
      <div className="w-full h-1.5 bg-white/10 rounded mb-4 overflow-hidden">
        <div
          className="h-full rounded bg-purple-500 transition-all duration-300"
          style={{ width: `${(checkedCount / 4) * 100}%` }}
        />
      </div>
      <ul className="list-none p-0 m-0">
        {ITEMS.map((item, i) => {
          const done = checked[i];
          const path = item.path;
          return (
            <li
              key={i}
              className="flex flex-row items-center gap-3 py-2 border-b border-white/5 last:border-b-0"
            >
              <span className={done ? '' : 'text-white/30'}>{done ? '✅' : '○'}</span>
              <span className={`text-sm ${done ? 'text-white' : 'text-white/60'}`}>{item.label}</span>
              {!done && path && (
                <button
                  type="button"
                  onClick={() => navigate(path)}
                  className="text-purple-400 text-xs hover:underline ml-auto whitespace-nowrap"
                >
                  {item.cta ?? '→ Add now'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SetupChecklist;
